import { ComponentProps, ForwardRefExoticComponent, RefAttributes, SVGProps } from 'react'

import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import { TrainingParticipantsQuery } from 'generated/resources/graphql'

import { buildUrl } from 'modules/core/lib/buildUrl'
import { i18nDateToString, stringToDate } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import { useTrainingsDatatable } from 'modules/trainings/hooks/useTrainingsDatatable'

type TrainingsTrainingMembership =
  TrainingParticipantsQuery['trainings']['trainingMembershipsConnection']['edges'][number]['node']
type OneDataCollectionProps = ComponentProps<typeof OneDataCollection>
type Visualizations = OneDataCollectionProps['visualizations']
type TableVisualization = Extract<Visualizations[number], { type: 'table' }>
type ColumnsType = TableVisualization['options']['columns'][number]
type ColumnType = Omit<ColumnsType, 'render' | 'sorting'> & {
  render: (item: TrainingsTrainingMembership) =>
    | string
    | { type: 'text'; value: string }
    | {
        type: 'person'
        value:
          | {
              firstName: string
              lastName: string
              src: string
            }
          | undefined
      }
    | {
        type: 'status'
        value: { status: string; label: string }
      }
    | {
        type: 'icon'
        value: {
          icon: ForwardRefExoticComponent<
            Omit<SVGProps<SVGSVGElement>, 'ref'> & RefAttributes<SVGSVGElement>
          >
          info: string
        }
      }
    | {
        type: 'percentage'
        value: { label: string; percentage: number }
      }
    | undefined
} & {
  sorting?: 'status' | 'trainingDueDate' | undefined
}

export interface ColumnConfig {
  isSessionsRequired: boolean
  isModulesRequired: boolean
  knowledgeTestRequired: boolean
  courseValidityEnabled: boolean
}

export const useColumns = (config: ColumnConfig): ColumnType[] => {
  const {
    getDataCollectionTrainingAttendanceColor,
    getTrainingAttendanceTranslation,
    mapTrainingAttendanceStatus,
    getDueDateText,
    getDueDateStatus,
  } = useTrainingsDatatable()

  const { knowledgeTestRequired, isSessionsRequired, isModulesRequired, courseValidityEnabled } =
    config

  return [
    {
      label: i18n.t('trainings.trainings_revamp.training_detail.employees_table.reviewed_name'),
      render: (item: TrainingsTrainingMembership) => ({
        type: 'person' as const,
        value: item.employee
          ? {
              firstName: item.employee.fullName ?? '',
              lastName: '',
              src: buildUrl(item.employee.avatar?.url),
            }
          : undefined,
      }),
    },
    {
      label: i18n.t('trainings.trainings_revamp.training_detail.employees_table.reviewed_status'),
      render: (item: TrainingsTrainingMembership) => {
        const mappedStatus = mapTrainingAttendanceStatus(item.status)
        return {
          type: 'status' as const,
          value: {
            status: getDataCollectionTrainingAttendanceColor(mappedStatus),
            label: getTrainingAttendanceTranslation(mappedStatus),
          },
        }
      },
      sorting: 'status' as const,
    },
    {
      label: i18n.t('trainings.trainings_revamp.training_detail.employees_table.certificate'),
      render: (item: TrainingsTrainingMembership) => {
        if (!item.trainingMembershipFiles.length || !item.employee) {
          return {
            type: 'text' as const,
            value: ' - ',
          }
        }
        const file = item.trainingMembershipFiles[0]
        if (!file) {
          return {
            type: 'text' as const,
            value: ' - ',
          }
        }
        const fileCount = item.trainingMembershipFiles.length
        const fileText =
          fileCount === 1
            ? `${fileCount} ${i18n.t(
                'trainings.trainings_revamp.training_detail.employees_table.file'
              )}`
            : `${fileCount} ${i18n.t(
                'trainings.trainings_revamp.training_detail.employees_table.files'
              )}`

        return {
          type: 'text' as const,
          value: fileText,
        }
      },
    },
    {
      label: i18n.t('trainings.trainings_revamp.training_detail.employees_table.completion_date'),
      render: (item: TrainingsTrainingMembership) => {
        const value = !item.trainingCompletedAt
          ? i18n.t(
              'trainings.trainings_revamp.training_detail.employees_table.empty_completion_date'
            )
          : i18nDateToString(
              stringToDate(item.trainingCompletedAt, 'backend_datetime'),
              'ui_abbreviated_month_year'
            )

        return {
          type: 'text' as const,
          value,
        }
      },
    },
    ...(courseValidityEnabled
      ? [
          {
            label: i18n.t(
              'trainings.trainings_revamp.training_detail.employees_table.due_date.title'
            ),
            render: (item: TrainingsTrainingMembership) => {
              const dueDateStatus = getDueDateStatus(item.trainingDueDate)
              if (!dueDateStatus) {
                return {
                  type: 'text' as const,
                  value: getDueDateText(item.trainingDueDate),
                }
              }

              return {
                type: 'status' as const,
                value: {
                  status: dueDateStatus.status,
                  label: dueDateStatus.label,
                },
              }
            },
            sorting: 'trainingDueDate' as const,
          },
        ]
      : []),
    ...(isSessionsRequired
      ? [
          {
            label: i18n.t(
              'trainings.trainings_revamp.training_detail.employees_table.session_attendance'
            ),
            render: (item: TrainingsTrainingMembership) => {
              const attended = item.attendedSessions ?? 0
              const total = item.totalSessions ?? 0
              const percentage = item.attendancePercentage ?? 0

              return {
                type: 'percentage' as const,
                value: {
                  percentage,
                  label: `${attended}/${total}`,
                },
              }
            },
          },
        ]
      : []),
    ...(knowledgeTestRequired
      ? [
          {
            label: i18n.t(
              'trainings.trainings_revamp.training_detail.employees_table.knowledge_test'
            ),
            render: (item: TrainingsTrainingMembership) => {
              if (item.knowledgeTestPassed === null || item.knowledgeTestPassed === undefined) {
                return {
                  type: 'status' as const,
                  value: {
                    status: 'warning',
                    label: i18n.t(
                      'trainings.trainings_revamp.training_detail.employees_table.knowledge_test_status.pending'
                    ),
                  },
                }
              }

              if (item.knowledgeTestPassed) {
                return {
                  type: 'status' as const,
                  value: {
                    status: 'positive',
                    label: i18n.t(
                      'trainings.trainings_revamp.training_detail.employees_table.knowledge_test_status.passed'
                    ),
                  },
                }
              }

              return {
                type: 'status' as const,
                value: {
                  status: 'critical',
                  label: i18n.t(
                    'trainings.trainings_revamp.training_detail.employees_table.knowledge_test_status.failed'
                  ),
                },
              }
            },
          },
        ]
      : []),
    ...(isModulesRequired
      ? [
          {
            label: i18n.t(
              'trainings.trainings_revamp.training_detail.employees_table.module_progress'
            ),
            render: (item: TrainingsTrainingMembership) => {
              const completed = item.completedModules ?? 0
              const total = item.totalModules ?? 0
              const percentage = total > 0 ? Number(((completed / total) * 100).toFixed(1)) : 0

              return {
                type: 'percentage' as const,
                value: {
                  percentage,
                  label: `${completed}/${total}`,
                },
              }
            },
          },
        ]
      : []),
  ]
}
